import React, { useContext, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { AuthContext } from "../context/AuthContext";
import { isEmail } from "../utils/validators";

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-svg">
      <path
        d="M3 17.25V21h3.75L19.81 7.94l-3.75-3.75L3 17.25z"
        fill="currentColor"
      />
      <path
        d="M20.71 6.04a1 1 0 0 0 0-1.41L19.37 3.29a1 1 0 0 0-1.41 0l-1.13 1.13 3.75 3.75 1.13-1.13z"
        fill="currentColor"
      />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-svg">
      <path
        fill="currentColor"
        d="M4 13h6V4H4Zm0 7h6v-6H4Zm10 0h6V11h-6Zm0-9h6V4h-6Z"
      />
    </svg>
  );
}

function AuthorIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-svg">
      <path
        fill="currentColor"
        d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4Z"
      />
    </svg>
  );
}

function JournalIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-svg">
      <path
        fill="currentColor"
        d="M6 4h12a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm0 2v14h12V6Z"
      />
    </svg>
  );
}

function TermsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-svg">
      <path
        fill="currentColor"
        d="M6 4h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm9 1.5V9h3.5L15 5.5ZM8 13h8v2H8Zm0 4h5v2H8Z"
      />
    </svg>
  );
}

function SignatureIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-svg">
      <path
        fill="currentColor"
        d="M4 19h16v2H4Zm2-7h12v2H6ZM4 8h8v2H4Z"
      />
    </svg>
  );
}

const PRIMARY_NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: <DashboardIcon /> }
];

const FORM_NAV_ITEMS = [
  { to: "/author-details", label: "Author Details", icon: <AuthorIcon /> },
  { to: "/journal-details", label: "Journal Details", icon: <JournalIcon /> },
  { to: "/terms-and-conditions", label: "Terms and Conditions", icon: <TermsIcon /> },
  { to: "/signature", label: "Signature", icon: <SignatureIcon /> }
];

export default function MainLayout() {
  const { user, updateUserName, requestEmailChangeOtp, verifyEmailChangeOtp } =
    useContext(AuthContext);
  const [openEditor, setOpenEditor] = useState("");
  const [nameValue, setNameValue] = useState(user?.name || "");
  const [newEmail, setNewEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const formPagePaths = [
    "/author-details",
    "/journal-details",
    "/terms-and-conditions",
    "/signature"
  ];
  const isFormPage = formPagePaths.includes(location.pathname);

  React.useEffect(() => {
    setNameValue(user?.name || "");
  }, [user?.name]);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleNameSave = async () => {
    clearMessages();
    const result = await updateUserName(nameValue);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess(result.message);
    setOpenEditor("");
  };

  const handleGetOtp = async () => {
    if (sendingOtp || otpCooldown > 0) return;
    clearMessages();
    if (!isEmail(newEmail)) {
      setError("Enter a valid new email.");
      return;
    }
    setSendingOtp(true);
    const result = await requestEmailChangeOtp(newEmail);
    setSendingOtp(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess(result.message);
    setOtpRequested(true);
    setOtpCooldown(30);
  };

  const handleEmailSave = async () => {
    clearMessages();
    const result = await verifyEmailChangeOtp({ otp });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess(result.message);
    setOpenEditor("");
    setOtp("");
    setOtpRequested(false);
    setOtpCooldown(0);
    setNewEmail("");
  };

  const otpLabel = sendingOtp
    ? "Sending..."
    : otpCooldown > 0
      ? `${otpCooldown}s`
      : otpRequested
        ? "Resend OTP"
        : "Get OTP";

  const displayName = useMemo(() => user?.name || "-", [user?.name]);
  const displayEmail = useMemo(() => user?.email || "-", [user?.email]);

  return (
    <section className="workspace-shell">
      <aside className="workspace-sidebar">
        <h3>Profile</h3>
        <div className="sidebar-profile-card">
          <div className="sidebar-profile-avatar-row">
            <UserAvatar name={user?.name} avatar={user?.avatar} size={40} />
          </div>
          <div className="sidebar-profile-details">
            <div className="sidebar-profile-detail-row">
              <strong className="sidebar-profile-value">{displayName}</strong>
              <button
                type="button"
                className="profile-edit-trigger"
                aria-label="Edit name"
                onClick={() => {
                  clearMessages();
                  setOpenEditor((prev) => (prev === "name" ? "" : "name"));
                  setNameValue(user?.name || "");
                }}
              >
                <PencilIcon />
              </button>
            </div>
            <div className="sidebar-profile-detail-row">
              <span className="sidebar-profile-value sidebar-profile-email">{displayEmail}</span>
              <button
                type="button"
                className="profile-edit-trigger"
                aria-label="Edit email"
                onClick={() => {
                  clearMessages();
                  setOpenEditor((prev) => (prev === "email" ? "" : "email"));
                }}
              >
                <PencilIcon />
              </button>
            </div>
          </div>

          {openEditor === "name" && (
            <div className="sidebar-profile-dropdown">
              <input
                className="toolbar-input"
                value={nameValue}
                onChange={(event) => setNameValue(event.target.value)}
                placeholder="Enter name"
              />
              <div className="sidebar-profile-actions">
                <button type="button" className="toolbar-btn compact-btn" onClick={() => setOpenEditor("")}>Close</button>
                <button type="button" className="toolbar-btn primary-btn compact-btn" onClick={handleNameSave}>Save</button>
              </div>
            </div>
          )}

          {openEditor === "email" && (
            <div className="sidebar-profile-dropdown">
              <input
                className="toolbar-input"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder="Enter new email"
              />
              <div className="sidebar-profile-otp-row">
                <input
                  className="toolbar-input"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  maxLength={6}
                  placeholder="OTP"
                />
                <button
                  type="button"
                  className="toolbar-btn compact-btn"
                  onClick={handleGetOtp}
                  disabled={sendingOtp || otpCooldown > 0}
                >
                  {otpLabel}
                </button>
              </div>
              <div className="sidebar-profile-actions">
                <button type="button" className="toolbar-btn compact-btn" onClick={() => setOpenEditor("")}>Close</button>
                <button type="button" className="toolbar-btn primary-btn compact-btn" onClick={handleEmailSave}>Update</button>
              </div>
            </div>
          )}
{error && <p className="submit-error profile-inline-msg">{error}</p>}
          {success && <p className="submit-success profile-inline-msg">{success}</p>}
        </div>

        <div className="sidebar-divider" />

          <nav>
            {PRIMARY_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `workspace-link${isActive ? " active" : ""}`
                }
              >
                <span className="workspace-link-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="sidebar-divider" />
        <section className="sidebar-group">
          <p className="sidebar-group-title">FORM DETAILS</p>
          <nav>
            {FORM_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `workspace-link${isActive ? " active" : ""}`
                }
              >
                <span className="workspace-link-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </section>
        <div className="new-form-row">
          <button
            type="button"
            className="toolbar-btn primary-btn"
            onClick={() => navigate("/author-details")}
          >
            New Submit
          </button>
        </div>
      </aside>

      <main className={`workspace-content${isFormPage ? " page-cardless" : ""}`}>
        <Outlet />
      </main>
    </section>
  );
}

